import { useState } from 'react'
import type { ComplianceCell } from '../api/client'
import { openDocument } from '../api/openBlob'
import './DocumentMark.css'

/**
 * The marker that says a scanned certificate sits behind this date.
 *
 * A link, not a badge. The point of attaching the scan was to be able to check
 * the date against the paper, and making that a two-click journey through the
 * detail page is most of the friction that stops anyone doing it. Opens in a new
 * tab so the register — filters, page, scroll — is still there afterwards.
 *
 * Never rendered for Permit: Permit takes no certificate, so a marker there would
 * attribute All India Permit's evidence to the wrong obligation.
 */
export function DocumentMark({
  vehicleId,
  registration,
  obligation,
  cell,
}: {
  vehicleId: number
  registration: string
  obligation: string
  cell: ComplianceCell
}) {
  const [busy, setBusy] = useState(false)

  const count = cell.documentCount ?? 0
  if (count === 0 || cell.currentDocumentId == null) return null

  // The certificate says one thing and the register says another. Worth marking:
  // this is the class of error the CarInfo/VAHAN episode exposed, and it is
  // invisible until something is holding both values at once.
  const disagrees =
    cell.documentExpiryDate != null &&
    cell.expiryDate != null &&
    cell.documentExpiryDate !== cell.expiryDate

  const title = [
    `${obligation} certificate on file for ${registration}`,
    cell.documentExpiryDate ? `The scan reads ${formatDate(cell.documentExpiryDate)}.` : null,
    disagrees
      ? `The register says ${formatDate(cell.expiryDate)} — these do not agree.`
      : null,
    count > 1 ? `${count} documents; this opens the current one.` : null,
    'Click to open in a new tab.',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      className={`docmark${disagrees ? ' docmark--disagrees' : ''}`}
      title={busy ? 'Opening…' : title}
      aria-label={title}
      disabled={busy}
      // A button, not a link: the bytes have to be fetched with a bearer token,
      // which a browser navigation cannot carry. The row navigates to the
      // vehicle, so opening the scan must not also change the page beneath it.
      onClick={(e) => {
        e.stopPropagation()
        setBusy(true)
        void openDocument(vehicleId, cell.currentDocumentId!).finally(() => setBusy(false))
      }}
    >
      <DocIcon />
      {count > 1 && <span className="docmark__count">{count}</span>}
    </button>
  )
}

/** Small enough to sit inside a table cell without displacing the date. */
function DocIcon() {
  return (
    <svg width="11" height="13" viewBox="0 0 11 13" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M1.6.8h5L9.9 4v7.4a.8.8 0 0 1-.8.8H1.6a.8.8 0 0 1-.8-.8V1.6a.8.8 0 0 1 .8-.8Z"
        fill="currentColor"
        opacity="0.2"
      />
      <path
        d="M1.6.8h5L9.9 4v7.4a.8.8 0 0 1-.8.8H1.6a.8.8 0 0 1-.8-.8V1.6a.8.8 0 0 1 .8-.8Z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M6.5.9v3.2h3.3" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'

  const [y, m, d] = iso.split('-')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  return `${d} ${months[Number(m) - 1]} ${y}`
}
